import { createContext, useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { T as T_DARK, T_LIGHT, makeUI } from '../styles/theme';
import { useBreakpoint } from '../hooks/useBreakpoint';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('red_dark_mode');
    return stored !== null ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    localStorage.setItem('red_dark_mode', JSON.stringify(isDark));
  }, [isDark]);

  // Theme PERSISTENT aufs DOM spiegeln (html[data-theme]). Wird sonst nirgends
  // ins DOM geschrieben (nur React-State + Inline-Styles) → der native
  // StatusBar-Bootstrap konnte das Theme nicht erkennen (las document.body, das
  // fix hell bleibt) und liess die iOS-Uhrzeit im Dark Mode dunkel/unsichtbar.
  // Vor dem Paint setzen, damit der Status-Bar-Style sofort mitzieht.
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Beim Theme-Wechsel app-weit ALLE CSS-Transitions fuer einen Frame
  // unterdruecken (siehe index.css `html[data-theme-switching]`), sonst fadet
  // jede Flaeche mit `transition: background/color` hinter dem sofort
  // umspringenden Rest her = Flackern. useLayoutEffect setzt das Attribut VOR
  // dem Paint mit den neuen Farben; zwei rAF spaeter wieder entfernen.
  const firstRun = useRef(true);
  useLayoutEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    const el = document.documentElement;
    el.setAttribute('data-theme-switching', '');
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => el.removeAttribute('data-theme-switching'));
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [isDark]);

  const T = isDark ? T_DARK : T_LIGHT;

  // Breakpoint-aware UI-Tokens: nur die MOBILE-Ansicht wird rund/iOS — Desktop
  // bekommt hier die scharfen Editorial-Werte (siehe makeUI). So bleibt der
  // Web-Look unangetastet, während Mobile via ui.radius/space/touch/shadow rundet.
  const { isMobile } = useBreakpoint();
  const ui = makeUI({ isMobile, isDark });

  return (
    <ThemeContext.Provider value={{ T, isDark, setIsDark, ui, isMobile }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
// Ergonomie: nur die UI-Token-Ebene (radius/space/touch/shadow + isMobile-Flag).
export const useUI = () => useContext(ThemeContext).ui;
